import React, { useEffect, useState } from 'react';

import {
  AlignLeftOutlined,
  FileOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Modal,
  Row,
  Skeleton,
  Tooltip,
  Tree,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { Company } from 'models/Company';
import { MenuNode } from 'models/MenuNode';

import { MenuService } from 'services/MenuService';

interface NewMenuDetailModalProps {
  company: Company;
  isOpen: boolean;
  handleModalCancel: () => void;
}

function NewMenuDetailModal({
  company,
  isOpen,
  handleModalCancel,
}: NewMenuDetailModalProps) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const [treeRenderKey, setTreeRenderKey] = useState<number>(0);
  const [rawNodes, setRawNodes] = useState<MenuNode[]>([]);
  const parentCodeMapRef = React.useRef<Map<string, string | 'root'>>(
    new Map()
  );
  const [form] = Form.useForm();

  useEffect(() => {
    if (isOpen) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isOpen]);

  const fetchData = async () => {
    setInitialLoading(true);

    const response = await MenuService.getSystemMenuNodesWithCompanyAccess(
      company.id
    );

    if (response.success) {
      const data = response.data as MenuNode[];
      // Construir treeData para Antd Tree
      const builtChildren = buildTreeData(data, undefined, i18n.language);
      const rootNode = {
        key: 'root',
        title: window.location.origin,
        selectable: false,
        children: builtChildren,
      };

      setRawNodes(data);
      setTreeData([rootNode]);
      setCheckedKeys([...collectSelectedKeys(data), 'root']);
      // Construir mapa de padres para propagación ascendente
      const parentMap = new Map<string, string | 'root'>();
      const buildParentMap = (arr: MenuNode[], parent: string | 'root') => {
        arr.forEach((n) => {
          parentMap.set(n.code, parent);
          if (n.children && n.children.length)
            buildParentMap(n.children, n.code);
        });
      };
      buildParentMap(data, 'root');
      parentCodeMapRef.current = parentMap;
      // Forzar remount del Tree para que defaultExpandAll se aplique
      setTreeRenderKey((prev) => prev + 1);
      setInitialLoading(false);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t(
            'scenes.settings.companies.components.new_menu_detail_modal.errors.fetch_menus_company'
          ),
        3.5
      );
    }
  };

  // Transforma MenuNode[] del backend a treeData de Antd
  const buildTreeData = (
    nodes: MenuNode[],
    parentCode?: string,
    currentLang?: string
  ): any[] => {
    const normalizedLang = currentLang === 'en' ? 'en' : 'es';
    return nodes.map((n) => {
      const localizedTitle = n.localizedTitles?.[normalizedLang] ?? null;
      const nodeTitle = localizedTitle ?? n.title;
      const children =
        Array.isArray(n.children) && n.children.length > 0
          ? buildTreeData(n.children, n.code, currentLang)
          : [];
      const iconEl =
        n.menuTypeId === 'PARENT_MENU' ? <FolderOutlined /> : <FileOutlined />;
      const isInherited = n.isRecursive === true;
      const titleNode = (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            maxWidth: '100%',
          }}
        >
          <span
            style={{
              flex: '1 1 auto',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {nodeTitle}
          </span>
          {isInherited && (
            <Tooltip
              title={t(
                'scenes.settings.companies.components.new_menu_detail_modal.tooltips.inherited_children_access'
              )}
            >
              <span
                style={{
                  backgroundColor: '#e6f4ff',
                  borderRadius: 10,
                  padding: '0 6px',
                  fontSize: 11,
                  color: '#0958d9',
                  lineHeight: '18px',
                  height: 18,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
                onClick={(e: React.MouseEvent<HTMLSpanElement>) =>
                  e.stopPropagation()
                }
                onMouseDown={(e: React.MouseEvent<HTMLSpanElement>) =>
                  e.stopPropagation()
                }
                onDoubleClick={(e: React.MouseEvent<HTMLSpanElement>) =>
                  e.stopPropagation()
                }
              >
                {t(
                  'scenes.settings.companies.components.new_menu_detail_modal.chips.inherited_children_access'
                )}
              </span>
            </Tooltip>
          )}
        </span>
      );
      return {
        title: titleNode,
        key: n.code,
        parentCode: parentCode,
        icon: iconEl,
        children,
      };
    });
  };

  // Re-renderizar árbol cuando cambie el idioma, manteniendo selección
  useEffect(() => {
    if (rawNodes && rawNodes.length > 0) {
      const builtChildren = buildTreeData(rawNodes, undefined, i18n.language);
      const rootNode = {
        key: 'root',
        title: window.location.origin,
        selectable: false,
        children: builtChildren,
      };
      setTreeData([rootNode]);
      // reconstruir mapa de padres al cambiar idioma (por consistencia)
      const parentMap = new Map<string, string | 'root'>();
      const buildParentMap = (arr: MenuNode[], parent: string | 'root') => {
        arr.forEach((n) => {
          parentMap.set(n.code, parent);
          if (n.children && n.children.length)
            buildParentMap(n.children, n.code);
        });
      };
      buildParentMap(rawNodes, 'root');
      parentCodeMapRef.current = parentMap;
      setTreeRenderKey((prev) => prev + 1);
    }
  }, [i18n.language]);

  // Obtiene keys seleccionadas según hasPermission
  const collectSelectedKeys = (nodes: MenuNode[]): React.Key[] => {
    const keys: React.Key[] = [];
    const walk = (arr: MenuNode[]) => {
      for (const n of arr) {
        if (n.hasPermission) keys.push(n.code);
        if (n.children && n.children.length) walk(n.children);
      }
    };
    walk(nodes);
    return keys;
  };

  const onFinish = async () => {
    setLoading(true);

    try {
      const onlyChecked = checkedKeys
        .filter((k) => k !== 'root')
        .map((k) => String(k));

      const response = await MenuService.associateMenusWithCompany({
        companyId: company.id,
        menus: onlyChecked,
      });

      if (response.success) {
        message.success(
          t(
            'scenes.settings.companies.components.new_menu_detail_modal.messages.save_success'
          ),
          2.5
        );
        handleModalCancel();
      } else {
        const err = response.data as ApiErrorData;
        message.error(
          (err?.message as string) ||
            t(
              'scenes.settings.companies.components.new_menu_detail_modal.errors.save_failed'
            ),
          3.5
        );
      }
    } catch (error: any) {
      message.error(
        t(
          'scenes.settings.companies.components.new_menu_detail_modal.errors.save_unexpected'
        ),
        3.5
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title={
          <>
            <AlignLeftOutlined style={{ marginRight: '5px' }} />
            {t(
              'scenes.settings.companies.components.new_menu_detail_modal.title',
              { companyName: company.name }
            )}
          </>
        }
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={640}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24} style={{ marginBottom: '18px' }}>
            <Col xs={24}>
              <Alert
                message={t(
                  'scenes.settings.companies.components.new_menu_detail_modal.alert_info'
                )}
                type="info"
                showIcon
              />
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: '18px' }}>
            <Col xs={24} lg={24}>
              {initialLoading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
              ) : (
                <Tree
                  key={treeRenderKey}
                  showLine
                  showIcon
                  checkable
                  checkStrictly
                  selectable={false}
                  defaultExpandAll
                  blockNode
                  style={{
                    pointerEvents: loading ? 'none' : 'auto',
                    opacity: loading ? 0.7 : 1,
                  }}
                  treeData={treeData}
                  checkedKeys={checkedKeys}
                  onCheck={(checkedParam, info) => {
                    if (loading) return; // desactivar interacción durante submit
                    // Lógica personalizada sin estados indeterminados
                    const nodeKey = info.node.key as string;
                    if (nodeKey === 'root') {
                      // Mantener siempre marcado el root
                      setCheckedKeys((prev) =>
                        Array.from(new Set([...prev, 'root']))
                      );
                      return;
                    }
                    const isBranch =
                      Array.isArray(info.node.children) &&
                      info.node.children.length > 0;
                    const next = new Set<React.Key>(checkedKeys);
                    const currentlyChecked = next.has(nodeKey);

                    const collectDescendantKeys = (node: any): React.Key[] => {
                      const acc: React.Key[] = [];
                      const walk = (n: any) => {
                        (n.children || []).forEach((child: any) => {
                          acc.push(child.key);
                          walk(child);
                        });
                      };
                      walk(node);
                      return acc;
                    };

                    if (!currentlyChecked) {
                      // Marcar nodo actual
                      next.add(nodeKey);
                      // Si es rama, marcar todos sus hijos
                      if (isBranch) {
                        collectDescendantKeys(info.node).forEach((k) =>
                          next.add(k)
                        );
                      }
                      // Propagar check hacia todos los ancestros hasta root
                      let parentCode = parentCodeMapRef.current.get(nodeKey);
                      while (parentCode && parentCode !== 'root') {
                        next.add(parentCode);
                        parentCode = parentCodeMapRef.current.get(parentCode);
                      }
                    } else {
                      // Desmarcar nodo actual
                      next.delete(nodeKey);
                      // Si es rama, desmarcar todos sus hijos
                      if (isBranch) {
                        collectDescendantKeys(info.node).forEach((k) =>
                          next.delete(k)
                        );
                      }
                    }

                    next.add('root');
                    setCheckedKeys(Array.from(next));
                  }}
                  onSelect={(keys, info) => {
                    if (loading) return; // desactivar interacción durante submit
                    // Al hacer clic en el título, alternamos el checkbox usando la misma lógica
                    const nodeKey = info.node.key as string;
                    if (nodeKey === 'root') {
                      // Mantener siempre marcado el root
                      setCheckedKeys((prev) =>
                        Array.from(new Set([...prev, 'root']))
                      );
                      return; // ignorar cambios adicionales
                    }

                    const isBranch =
                      Array.isArray(info.node.children) &&
                      info.node.children.length > 0;
                    const next = new Set<React.Key>(checkedKeys);
                    const currentlyChecked = next.has(nodeKey);

                    const collectDescendantKeys = (node: any): React.Key[] => {
                      const acc: React.Key[] = [];
                      const walk = (n: any) => {
                        (n.children || []).forEach((child: any) => {
                          acc.push(child.key);
                          walk(child);
                        });
                      };
                      walk(node);
                      return acc;
                    };

                    if (!currentlyChecked) {
                      // Marcar nodo actual
                      next.add(nodeKey);
                      // Si es rama, marcar todos sus hijos
                      if (isBranch) {
                        collectDescendantKeys(info.node).forEach((k) =>
                          next.add(k)
                        );
                      }
                      // Propagar check hacia todos los ancestros hasta root
                      let parentCode = parentCodeMapRef.current.get(nodeKey);
                      while (parentCode && parentCode !== 'root') {
                        next.add(parentCode);
                        parentCode = parentCodeMapRef.current.get(parentCode);
                      }
                    } else {
                      // Desmarcar nodo actual
                      next.delete(nodeKey);
                      // Si es rama, desmarcar todos sus hijos
                      if (isBranch) {
                        collectDescendantKeys(info.node).forEach((k) =>
                          next.delete(k)
                        );
                      }
                    }

                    next.add('root');
                    setCheckedKeys(Array.from(next));
                  }}
                />
              )}
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => {
                  form.submit();
                }}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.settings.companies.components.new_menu_detail_modal.buttons.save_changes'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default NewMenuDetailModal;
